import './config/habitat/conditions'; // registers the habitat condition predicates

import { guidedJourneyConfig } from './config/guidedJourney';
import { guidedJourneyEndedConfig } from './config/guidedJourneyEnded';
import { ASLEEP_FIRST_STEP_ID, habitatAsleepConfig } from './config/habitat/asleep';
import {
  HABITAT_ONBOARDING_ASLEEP_ID,
  HABITAT_ONBOARDING_ONLINE_ID,
  HABITAT_ONBOARDING_OUTAGE_ID,
} from './config/habitat/ids';
import { habitatOnlineConfig, ONLINE_FIRST_STEP_ID } from './config/habitat/online';
import { habitatOutageConfig, OUTAGE_FIRST_STEP_ID } from './config/habitat/outage';
import { isCompeting } from './core/arbitrate';
import { evaluateCond } from './core/conditions';
import { route } from './core/route';
import { deriveStatus } from './core/status';
import { CondResult, TourConfig, TourContext, TourGateState, TourRegistration, TourStep } from './types';

export const GUIDED_JOURNEY_ID = 'guidedJourney';
export const GUIDED_JOURNEY_ENDED_ID = 'guidedJourneyEnded';

export { HABITAT_ONBOARDING_IDS } from './config/habitat/ids';

// Lightweight inline of selectHasGuidedExperienceFeature (kept dependency-free so the
// registry doesn't drag the heavy selectors module into tour tests).
const hasGuidedExperience = (gate: TourGateState): boolean => {
  const until = gate.featuresUntil?.guidedExperience;
  return Boolean(until && Date.parse(until) > Date.now());
};

const guidedJourney: TourRegistration = {
  id: GUIDED_JOURNEY_ID,
  name: 'All Access Guided Tour', // pre-existing analytics name
  config: guidedJourneyConfig,
  scope: 'global',
  exclusive: true,
  priority: 100,
  firstStepId: '0-modal',
  isEligible: hasGuidedExperience,
  // Auto-starts for an eligible user landing on /home.
  autoStart: (_gate, pathname) => pathname === '/home',
};

/**
 * Is the guided journey holding the screen? The habitat onboardings wait for it to finish
 * rather than competing with it — two guides talking at once helps nobody.
 *
 * Asking `deriveStatus(...) === 'active'` alone would be a bug. A user whose `guidedExperience`
 * feature lapses mid-journey keeps a stepHistory with no completedAt/skippedAt, so the journey
 * reads `active` forever — and habitat onboarding would be blocked permanently. Its `isEligible`
 * is what actually retires it, so the shared `isCompeting` (the arbiter's own candidate test) is
 * the only correct question.
 */
const journeyHoldsTheScreen = (gate: TourGateState, ctx: TourContext | undefined, pathname: string): boolean =>
  isCompeting(guidedJourney, gate, ctx, pathname);

/**
 * One registration per habitat scenario. Three tours rather than one because each has to keep
 * its own position: leaving an online habitat mid-flow for an offline one, then coming back,
 * should resume where the user left off in BOTH. Separate `stepHistory` per tour gives that for
 * free, and only one is ever eligible, so they never compete.
 *
 * There is no bespoke `autoStart`: eligibility answers `pending` until the habitat page has
 * published its context, and starting waits for a definite `true`.
 */
const habitatOnboarding = (id: string, config: TourConfig, firstStepId: string, cond: string): TourRegistration => ({
  id,
  name: 'Habitat Onboarding', // one analytics identity across the three scenarios
  config,
  scope: route('/h/*/*'),
  exclusive: false,
  // Above the post-journey banner (10) so this owns the habitat page, below the journey (100),
  // which is exclusive anyway.
  priority: 50,
  firstStepId,
  isEligible: (gate, ctx, pathname): CondResult => {
    if (journeyHoldsTheScreen(gate, ctx, pathname)) return false;
    // A modal is holding the screen: step aside (not `pending` — a modal is a definite state)
    // and resume at the same step once it closes.
    if (ctx?.blockingModalOpen) return false;
    return evaluateCond(cond, ctx);
  },
  autoStart: () => true,
});

const habitatOnboardings: TourRegistration[] = [
  habitatOnboarding(HABITAT_ONBOARDING_ONLINE_ID, habitatOnlineConfig, ONLINE_FIRST_STEP_ID, 'habitatOnline'),
  habitatOnboarding(HABITAT_ONBOARDING_ASLEEP_ID, habitatAsleepConfig, ASLEEP_FIRST_STEP_ID, 'habitatAsleep'),
  habitatOnboarding(HABITAT_ONBOARDING_OUTAGE_ID, habitatOutageConfig, OUTAGE_FIRST_STEP_ID, 'habitatOutage'),
];

/**
 * The tour registry. Adding a tour = one entry here + an authored config array.
 * `scope` + `exclusive` + `priority` drive the arbiter (see useTourArbiter).
 */
export const TOURS: TourRegistration[] = [
  guidedJourney,
  {
    // The post-tour banner. Non-exclusive and low priority, so habitat/events onboardings win
    // on their own pages; this still shows anywhere nothing else is competing. Eligibility is
    // tied to the journey staying completed, so restarting the journey removes it.
    id: GUIDED_JOURNEY_ENDED_ID,
    // Same analytics identity as the journey — it is the same user-facing tour continuing —
    // but starting it is not a new tour start, so it emits no 'triggered' event.
    name: 'All Access Guided Tour',
    trackStart: false,
    config: guidedJourneyEndedConfig,
    scope: 'global',
    exclusive: false,
    priority: 10,
    firstStepId: 'End2',
    // gate.tours is the merged view, so a journey completed on another device counts here too.
    isEligible: (gate) => hasGuidedExperience(gate) && deriveStatus(gate.tours[GUIDED_JOURNEY_ID]) === 'completed',
    autoStart: () => true,
  },
  ...habitatOnboardings,
];

const TOUR_BY_ID = new Map(TOURS.map((t) => [t.id, t]));

export const getTour = (tourId: string): TourRegistration | undefined => TOUR_BY_ID.get(tourId);

// id -> step lookup per tour, built once at module load. The entries are references to the
// authored step objects, so this costs a couple of Maps and no duplication. The config array
// stays the source of truth (order included); no separate graph structure is needed.
const STEPS_BY_TOUR = new Map(TOURS.map((tour) => [tour.id, new Map(tour.config.steps.map((step) => [step.id, step]))]));

export const getStep = (tourId: string, stepId: string): TourStep | undefined => STEPS_BY_TOUR.get(tourId)?.get(stepId);
